<template>
  <div class="payment-options">
    <KycVerificationRequired
      v-if="kycRequired && !kycVerified"
      :key="kycGateKey"
      :is-guest-user="isGuestCheckout"
      class="payment-options__kyc"
      @verified="$emit('verified')"
    />
    <template v-else>
      <h2 class="d-flex flex-row justify-content-between align-items-center">
        <div>{{ $t("payment.choose-wallet") }}</div>
      </h2>
      <div class="payment-option-inner" v-if="selectedTabIndex > -1">
        <b-tabs
          small
          justified
          active-nav-item-class="active-tab"
          active-tab-class="active-tab_content"
          @activate-tab="activateTab"
          v-model="selectedTabIndex"
        >
          <!--Crypto exchange-->
          <b-tab
            lazy
            v-if="filteredTab.includes(cryptoExchangeName)"
            :title="cryptoExchangeName"
            :title-link-class="'link-class'"
            class="tab-item"
            :id="cryptoExchangeName"
            :disabled="
              cryptoPaymentStatus == 'partial' ||
              isContinuedTransaction ||
              isInProcess
            "
            @click="handleChange('Exchange')"
          >
            <template #title>
              <img
                v-if="selectedTab === cryptoExchangeName"
                src="@/assets/img/tabs/exchangeTabWhite.svg"
                alt=""
              />
              <img v-else src="@/assets/img/tabs/exchangeTabBlack.svg" alt="" />
              <span class="ml-1 mt-1">{{ cryptoExchangeName }}</span>
            </template>
            <transition name="fade">
              <div class="tab-data__wrapper">
                <div class="tab-data__content no-top-border">
                  <div v-if="selectedTab === cryptoExchangeName">
                    <ExchangesV2
                      :isPreview="isPreview"
                      :displayCurrency="displayCurrency"
                      :convertedCart="convertedCart"
                      :cartData="cartData"
                      @setLoader="setLoader"
                    />
                  </div>
                </div>
              </div>
            </transition>
          </b-tab>
          <!--Crypto Wallet (not lazy — WalletConnect must mount when tab is first shown)-->
          <b-tab
            v-if="filteredTab.includes(cryptoWalletName)"
            :title="cryptoWalletName"
            :id="cryptoWalletName"
            :title-link-class="'link-class'"
            class="tab-item"
            @click="handleChange('Wallet')"
          >
            <template #title>
              <img
                v-if="selectedTab === cryptoWalletName"
                src="@/assets/img/tabs/walletTabWhite.svg"
                alt=""
              />
              <img v-else src="@/assets/img/tabs/walletTabBlack.svg" alt="" />
              <span class="ml-1 mt-1">{{ cryptoWalletName }}</span>
            </template>
            <transition name="fade">
              <div class="crypto-wallet-tab-panel">
                <wallet-connect
                  v-if="showWalletConnect"
                  :key="walletConnectRenderKey"
                  :cartData="cartData"
                  @walletConnected="walletConnected"
                />
                <div
                  v-if="showWalletConnect && !isWalletConnected"
                  class="wallet-pay-split"
                >
                  <div class="wallet-pay-split__divider" role="separator">
                    <span>{{ $t("walletconnect.orPayWithCoin") }}</span>
                  </div>
                  <p class="wallet-pay-split__heading">
                    {{ $t("unauth-payment.choose-currency") }}
                  </p>
                </div>
                <crypto-wallet
                  v-if="!isWalletConnected"
                  :isContinuedTransaction="isContinuedTransaction"
                  :isPreview="isPreview"
                  :walletOptions="walletOptions"
                  :selectedTab="selectedTab === cryptoWalletName"
                  @setLoader="setLoader"
                  @paymentConfirmed="isPaymentMethodVisible = false"
                  :navigateToPreviousStep="navigateToPreviousStep"
                  :cartData="cartData"
                  @cryptoPaymentStatus="setCryptoPaymentStatus"
                  @anyWalletCoin="onWalletCoinChange"
                  @qrCodeShown="qrCodeShown"
                />
              </div>
            </transition>
          </b-tab>
          <!--Bank-->
          <b-tab
            lazy
            :disabled="
              cryptoPaymentStatus == 'partial' ||
              isContinuedTransaction ||
              isInProcess
            "
            v-if="
              filteredTab.includes(bankTabName) &&
              settings.displayCurrency &&
              settings.displayCurrency.toLowerCase() === 'usd' &&
              !isGuestCheckout
            "
            :title="bankTabName"
            :title-link-class="'link-class'"
            :id="bankTabName"
            class="tab-item"
            @click="handleChange('Bank')"
          >
            <template #title>
              <img
                v-if="selectedTab === bankTabName"
                src="@/assets/img/tabs/bankTabWhite.svg"
                alt=""
              />
              <img
                v-else-if="isSubscriptionTabEnabled"
                src="@/assets/img/tabs/bankTabGrey.svg"
                alt=""
              />
              <img v-else src="@/assets/img/tabs/bankTabBlack.svg" alt="" />
              <span class="ml-1 mt-1">{{ bankTabName }}</span>
            </template>
            <transition name="fade">
              <Bank @setLoader="setLoader" :rootConvertedData="convertedCart" />
            </transition>
          </b-tab>
          <b-tab
            lazy
            v-if="filteredTab.includes(buyTab) && !isGuestCheckout"
            :title="buyTab"
            :title-link-class="'link-class'"
            :id="buyTab"
            class="tab-item"
            @click="handleChange('Onramp')"
            :disabled="
              cryptoPaymentStatus == 'partial' ||
              isContinuedTransaction ||
              isInProcess
            "
          >
            <template #title>
              <img
                v-if="selectedTab === buyTab"
                src="@/assets/img/tabs/coins-white.svg"
                alt=""
                class="logo-i"
              />
              <img
                v-else
                src="@/assets/img/tabs/coins.svg"
                alt=""
                class="logo-i"
              />
              <span class="ml-1 mt-1">{{ buyTab }}</span>
            </template>
            <transition name="fade">
              <sardine @setLoader="setLoader" />
            </transition>
          </b-tab>
        </b-tabs>
      </div>
      <div
        v-if="!isPreview && marketInfo?.enabledLoyalty && opted?.optIn"
        class="payment-options__info-box mt-1 p-2"
      >
        <div class="d-flex justiy-content-between align-items-center w-100">
          <div class="w-75">
            <div>
              <div class="payment-options__rewards-balance">
                <span class="payment-options__rewards-label">{{
                  $t("rewards.balance")
                }}</span>
                <span class="payment-options__rewards-points"
                  >{{ rewardsBalance?.allocated || 0 }}
                  {{ $t("rewards.points") }}</span
                >
              </div>
              <div class="payment-options__rewards-earn">
                <span class="payment-options__rewards-label">{{
                  $t("rewards.earnOnOrder")
                }}</span>
                <span class="payment-options__rewards-points"
                  >{{ points }} {{ $t("rewards.points") }}</span
                >
              </div>
            </div>
          </div>

          <div class="w-25 d-flex justify-content-end">
            <img
              class="w-50 payment-options__token"
              src="@/assets/img/price-tag.png"
              alt="token"
            />
          </div>
        </div>

        <!-- Login to Rewards Link - Centered -->
        <div class="text-center mt-2">
          <a
            href="#"
            class="payment-options__login-link"
            @click="openRewardsAccount"
          >
            {{ $t("rewards.loginToRewards") }}
          </a>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
/* eslint-disable global-require */

import { BTabs, BTab } from "bootstrap-vue";
import { mapGetters, useStore } from "vuex";
const ExchangesV2 = () => import("@/components/ExchangesV2.vue");
const Bank = () => import("@/components/Bank.vue");
import KycVerificationRequired from "@/components/KycVerificationRequired.vue";
import {
  SET_TAB,
  USER_TRACK_EVENTS,
  CHECK_REWARDS_OPTIN_STATUS,
  ENROLL_REWARDS_PROGRAM,
  ESITMATE_REWARDS,
  REWARD_SIGN_UP,
  GET_USER_INFO,
  GET_REWARDS_BALANCE,
} from "@/store/modules/user/consts";
import {
  BANKS_TAB,
  CRYPTO_TAB,
  WALLET_TAB,
  BUY_CRYPTO,
} from "../utils/stringconsts";
import { computed } from "vue";
import { enableWalletConnect as enableWalletConnectEnv } from "@/utils/config";
import {
  shouldFetchRewardsOptIn,
  shouldFetchUserInfo,
} from "@/utils/sessionDataCache";

export default {
  components: {
    BTabs,
    BTab,
    ExchangesV2,
    Bank,
    KycVerificationRequired,
  },
  props: {
    walletOptions: Array || [],
    convertedCart: Object || {},
    displayCurrency: Object || {},
    isPreview: Boolean || false,
    cartData: Object || {},
    isContinuedTransaction: Boolean || false,
    kycRequired: { type: Boolean, default: false },
    kycVerified: { type: Boolean, default: false },
    kycGateKey: { type: String, default: "kyc" },
    isGuestUser: { type: Boolean, default: false },
  },
  setup() {
    const store = useStore();
    const marketInfo = computed(() => store.getters["user/marketInfo"]);

    // Must be computed: marketInfo loads after mount (iframe postMessage / getData).
    const enableWalletConnect = computed(
      () => !!marketInfo.value?.walletconnectEnabled || enableWalletConnectEnv
    );

    return { enableWalletConnect };
  },
  data() {
    return {
      anyWalletCoin: {},
      cryptoWalletName: WALLET_TAB,
      cryptoExchangeName: CRYPTO_TAB,
      bankTabName: BANKS_TAB,
      buyTab: BUY_CRYPTO,
      selectedExchange: null,
      isPaymentMethodVisible: true,
      warningModal: false,
      selectedTabIndex: -1,
      uniqueTabs: [],
      skipRender: false,
      navigateToPreviousStep: false,
      cryptoPaymentStatus: "",
      isSavePayment: true,
      isInProcess: false,
      points: 0,
      showModal: false,
      isWalletConnected: false,
      qrPage: false,
      walletConnectRenderKey: 0,
    };
  },
  watch: {
    selectedTab(switchedTab) {
      this.$store.dispatch(`user/${SET_TAB}`, switchedTab);
    },
    filteredTab: {
      handler(paymentTabs) {
        this.syncPaymentTabs(paymentTabs);
      },
      deep: true,
      immediate: true,
    },
    enableWalletConnect(enabled, wasEnabled) {
      if (enabled && wasEnabled === false) {
        this.walletConnectRenderKey += 1;
      }
    },
    "marketInfo.walletconnectEnabled"(enabled) {
      if (enabled || enableWalletConnectEnv) {
        this.walletConnectRenderKey += 1;
      }
    },
    cryptoWalletStep(val) {
      if (val === 2) {
        this.isPaymentMethodVisible = false;
      } else this.isPaymentMethodVisible = true;
    },
    opted: {
      handler(val) {
        if (this.isPreview) {
          return;
        }
        if (val?.optIn) {
          // dont open
          this.$store.dispatch(`user/${GET_REWARDS_BALANCE}`);
          this.showModal = false;
        } else if (this.marketInfo?.enabledLoyalty) {
          // open
          if (!this.show) this.showModal = true;
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.isPreview) {
      return;
    }
    this.$nextTick(async () => {
      const state = this.$store.state.user;
      const merchantId = this.marketInfo?.merchant?.id;
      const tasks = [];

      if (shouldFetchUserInfo(state)) {
        tasks.push(this.$store.dispatch(`user/${GET_USER_INFO}`));
      }
      if (shouldFetchRewardsOptIn(state, merchantId)) {
        tasks.push(this.checkUserHasOptedForRewards());
      }
      if (this.cartData?.amount) {
        tasks.push(this.getEstimatedRewards());
      }
      if (tasks.length) {
        await Promise.all(tasks);
      }
    });
  },
  computed: {
    ...mapGetters({
      marketInfo: "user/marketInfo",
      tab: "user/tab",
      cart: "user/cart",
      settings: "user/settings",
      currencies: "user/currencies",
      cryptoWalletStep: "user/getCryptoWalletStep",
      opted: "user/getEnrolledInRewards",
      userInfo: "user/userInfo",
      show: "user/getRewardModal",
      continuedTransaction: "user/getContinuedTransaction",
      rewardsBalance: "user/getRewardsBalance",
    }),
    isFasterPaymentsEnabled() {
      if (this.currencies && this.currencies.length) {
        const filtered = this.currencies.filter(
          (val) => val.fasterConfirmation
        );
        return !!(filtered && filtered.length);
      }
      return false;
    },
    isSubscriptionTabEnabled() {
      if (
        this.convertedCart &&
        this.convertedCart.cart &&
        this.convertedCart.cart.length
      ) {
        const subs = this.convertedCart.cart.some((el) => el.isSubscription);
        return subs;
      }
      return false;
    },
    isGuestCheckout() {
      if (this.isGuestUser) {
        return true;
      }
      if (this.$DbStorage.getItem("isGuestUser")) {
        return JSON.parse(this.$DbStorage.getItem("isGuestUser"));
      }
      return false;
    },
    filteredTab() {
      if (
        this.marketInfo &&
        Object.keys(this.marketInfo).length &&
        Object.keys(this.marketInfo.settings).length &&
        this.marketInfo.settings.tabs.length
      ) {
        return this.marketInfo.settings.tabs;
      }
      return [];
    },
    selectedTab() {
      return this.uniqueTabs[this.selectedTabIndex];
    },
    isMarketInfoReady() {
      return !!(this.marketInfo && Object.keys(this.marketInfo).length);
    },
    showWalletConnect() {
      if (
        !this.enableWalletConnect ||
        this.qrPage ||
        this.isContinuedTransaction
      ) {
        return false;
      }
      // Merchant flag comes from GET_MARKET_INFO — avoid mounting before it arrives.
      if (!enableWalletConnectEnv && !this.isMarketInfoReady) {
        return false;
      }
      return this.selectedTab === this.cryptoWalletName;
    },
  },
  methods: {
    syncPaymentTabs(paymentTabs) {
      if (!paymentTabs?.length) {
        return;
      }
      this.uniqueTabs = [...new Set(paymentTabs)];
      const tab = this.loadInitialTab();
      const idx = this.uniqueTabs.indexOf(tab);
      this.selectedTabIndex = idx < 0 ? 0 : idx;
    },
    checkUserHasOptedForRewards() {
      return this.$store.dispatch(`user/${CHECK_REWARDS_OPTIN_STATUS}`);
    },
    qrCodeShown(val) {
      this.qrPage = val;
    },
    walletConnected(val) {
      this.isWalletConnected = val;
    },
    async handleChange(text) {
      this.$store.dispatch(`user/${USER_TRACK_EVENTS}`, {
        moduleInfo: {
          current: text,
          activity: `clicked on ${text} tab`,
        },
      });
    },
    setCryptoPaymentStatus(val) {
      this.cryptoPaymentStatus = val;
    },
    loadInitialTab() {
      if (this.tab) {
        return this.tab;
      }

      if (this.settings && Object.keys(this.settings).length) {
        if (this.settings.method === "crypto") {
          if (
            this.settings.stockId &&
            this.filteredTab.includes(this.cryptoExchangeName)
          ) {
            return this.cryptoExchangeName;
          }
          if (this.filteredTab.includes(this.cryptoWalletName)) {
            return this.cryptoWalletName;
          }
        }
        if (
          this.settings.method === "fiat" &&
          this.filteredTab.includes(this.bankTabName)
        ) {
          return this.bankTabName;
        }
        return this.cryptoWalletName;
      }
      return this.cryptoWalletName;
    },
    getConnectBank() {
      this.$router.push({ name: "AddBank" });
    },
    setLoader(val) {
      this.$emit("handleLoader", val);
    },
    onWalletCoinChange(val) {
      this.anyWalletCoin = val;
    },
    async activateTab(newIndex, oldIndex, event) {
      if (this.skipRender) {
        this.skipRender = false;
        return false;
      }

      if (!this.isPaymentMethodVisible) {
        const walletName =
          this.anyWalletCoin && this.anyWalletCoin.name
            ? this.anyWalletCoin.name
            : "crypto";
        let warnMsg = this.$t("warning-modal.tabs-change");
        warnMsg = warnMsg.replace("[crypto]", ` ${walletName}`);
        event.preventDefault();
        const h = this.$createElement;
        const messageVNode = h("div", { class: ["custom-modal-body"] }, [
          h("div", { class: ["d-flex"] }, [
            h("img", {
              class: ["modal-img mr-2"],
              attrs: { src: require("@/assets/img/bx_info-circle.svg") },
            }),
            h("span", warnMsg),
          ]),
        ]);
        const navigateToNextTab = await this.$bvModal.msgBoxConfirm(
          messageVNode,
          {
            title: this.$t("warning-modal.tabs-change-title"),
            size: "sm",
            buttonSize: "sm",
            cancelTitle: this.$t("warning-modal.proceed"),
            headerClass: "custom-modal-header",
            footerClass: "custom-footer-btns",
            okTitle: this.$t("warning-modal.dismiss"),
          }
        );
        this.$store.dispatch(`user/${USER_TRACK_EVENTS}`, {
          moduleInfo: {
            current: "Crypto Wallet tab",
            activity: "change currency popup opened",
          },
        });
        if (!navigateToNextTab) {
          this.selectedTabIndex = newIndex;
          this.skipRender = true;
          this.isPaymentMethodVisible = true;
          this.navigateToPreviousStep = true;
          this.$store.commit("user/setCryptoWalletStep", 1);
        } else {
          this.selectedTabIndex = oldIndex;
          this.skipRender = false;
        }
      }
      return false;
    },
    async enrollForRewards() {
      if (this.userInfo.ssoStatus && !this.userInfo.verified) {
        this.showModal = false;
        window.EventBus.emit("showModal", { type: REWARD_SIGN_UP });
      } else {
        try {
          await this.$store.dispatch(`user/${ENROLL_REWARDS_PROGRAM}`);
          this.showModal = false;
          setTimeout(this.checkUserHasOptedForRewards, 3000);
        } catch (err) {
          // error
        }
      }
    },
    async getEstimatedRewards() {
      const paylaod = {
        amount: this.cartData?.amount,
        currency: this.cartData.merchantStoreCurrency,
      };
      const data = await this.$store.dispatch(
        `user/${ESITMATE_REWARDS}`,
        paylaod
      );
      this.points = data?.points || 0;
    },
    openRewardModal() {
      this.showModal = true;
    },
    openRewardsAccount() {
      // Open rewards account login
      const loyaltyUrl =
        process.env.VUE_APP_LOYALTY_URL || "https://loyalty.rocketfuel.inc/";
      window.open(loyaltyUrl, "_blank");
    },
    closeModal() {
      this.showModal = false;
    },
    // placeOrderOnramp() {
    //   this.setValue({ connect: 'onramp' });
    // },
  },
};
</script>
<style lang="scss">
.crypto-wallet-tab-panel {
  background: #fff;
  padding: 12px;
  border: 1px solid #ededee;
  border-top: none;
  border-radius: 0 0 4px 4px;

  .walletconnect {
    background: transparent;
  }
}

.wallet-pay-split {
  margin: 14px 0 10px;
  padding: 0;
  background: #fff;

  &__divider {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;

    &::before,
    &::after {
      content: "";
      flex: 1;
      height: 1px;
      background: #dadada;
    }

    span {
      font-size: 11px;
      font-weight: 600;
      line-height: 1.2;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #888786;
      white-space: nowrap;
    }
  }

  &__heading {
    margin: 0 0 8px;
    padding: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: #1b1f3b;
    text-align: left;
    background: #fff;
  }
}

.payment-options .crypto-wallet-tab-panel .payment-options {
  background: transparent;
}

.payment-options {
  &__info-box {
    border: 1px solid #2626dd8c;
    font-size: 13px;
    width: 100%;
    padding: 5px;
    border-radius: 8px;
    color: #2726dd;
    font-weight: 600;
    background: #f8fafc;
    font-family: "Arial", "Helvetica Neue", "Helvetica", sans-serif;
  }

  &__rewards-balance,
  &__rewards-earn {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
  }

  &__rewards-label {
    color: #000000;
    font-size: 14px;
    font-weight: 400;
    font-family: "Arial", "Helvetica Neue", "Helvetica", sans-serif;
    margin-right: 8px;
  }

  &__rewards-points {
    color: #5559fe;
    font-size: 14px;
    font-weight: 400;
    font-family: "Arial", "Helvetica Neue", "Helvetica", sans-serif;
  }

  &__token {
    padding: 5px;
  }

  &__login-link {
    color: #3b82f6;
    text-decoration: underline;
    font-weight: 400;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      color: #2563eb;
      text-decoration: none;
    }
  }
  &__kyc {
    margin-bottom: 16px;
  }
  &__learn {
    font-size: 13px;
    font-weight: 400;
    color: #000000;
    text-decoration: underline #000000;
    font-family: "Arial", "Helvetica Neue", "Helvetica", sans-serif;
  }
  &__learnmore {
    color: #000000;
    text-decoration: underline #000000;
    font-size: 13px;
    font-weight: 400;
    font-family: "Arial", "Helvetica Neue", "Helvetica", sans-serif;
  }
  .nav-tabs {
    background-color: $white;
  }

  // margin-bottom: 40px;
  .link-class {
    display: flex !important;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    padding: 0px !important;
    color: #000000;
    font-size: 10px;
    white-space: nowrap;
    position: relative;
    font-weight: 500;
    height: 34px;
    background-color: #f1f1f1;
    border: 1px solid #dadada !important;
    border-top-left-radius: 15px !important;
    border-top-right-radius: 15px !important;

    &:hover {
      border-bottom: none !important;
    }
  }

  h2 {
    font-size: 15px;
    line-height: 25px;
    color: #000;
    font-weight: 600;
    margin: 0;
    margin-bottom: 8px;
  }

  .payment-option-inner {
    border-radius: 6px;
    background: #ededee;

    .tab-data__wrapper {
      background-color: #fff;

      .tab-data__content {
        border: 1px solid #ededee;
        border-radius: 4px;
        padding: 12px;
        border-top: none;
      }

      // .tab-data__connect-bank {
      //   align-items: center;
      //   justify-content: center;
      //   display: grid;
      //   img {
      //   margin: auto;
      //   max-width: 80px;
      //   }
      //   p {
      //   font-size: 12px;
      //   color: #1E1C41;
      //   text-align: center;
      //   }
      //   &__btn {
      //   width: 60%;
      //   margin: 10px auto;
      //   padding: 2px;
      //   font-size: 14px
      //   }
      // }
    }
  }
}

.active-tab {
  // border-top: 3px solid #5559FE !important;
  color: #fff !important;
  border-collapse: collapse;
  background: #5559fe !important;
  height: 40px;
  border: 1px solid #5559fe !important;

  &_content {
    border-collapse: collapse;
  }
}

.logo {
  width: 25px;
  cursor: pointer;
  animation: bounce 0.5s ease infinite;
}

.logo-i {
  width: 20px;
}

.font-weight-400 {
  font-weight: 500 !important;
  font-size: 12px;
  color: #000000;
}

@keyframes bounce {
  0% {
    transform: translate3d(0px, 0px, 0);
  }

  50% {
    transform: translate3d(0px, -2px, 0);
  }

  100% {
    transform: translate3d(0px, 0px, 0);
  }
}

.my-tooltip-class {
  color: #fff;

  .tooltip-inner {
    max-width: 200px;
    padding: 0.25rem 0.5rem;
    color: #fff !important;
    text-align: left;
    background-color: #000000 !important;
    border-radius: 0.25rem;
    font-family: "Mukta", sans-serif;
    font-size: 11px;
    border: 1px solid #000000 !important;
  }
}

.custom-modal-header {
  padding: 5px 10px !important;
  border-bottom: none !important;

  .modal-title {
    font-size: 14px;
    font-weight: 700;
    padding: 15px;
    margin-bottom: -15px;
  }
}

.custom-modal-body {
  background: rgba(0, 0, 0, 0.05);
  padding: 10px;
  font-size: 13px;
}

.custom-footer-btns {
  .btn {
    padding: 0 10px;
  }

  .btn-secondary {
    cursor: pointer;
    border-radius: 5px;
    color: #303030;
    margin: 0px 5px;
    border: 1px solid #5559fe;
    background: white;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
</style>
